import { APP_BASE_HREF } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Observable, from, shareReplay } from 'rxjs';
import { mergeEmojiData } from './chat-emoji-merge';
import { ChatEmojiData } from '../model/chat-emoji';

@Injectable({
  providedIn: 'root'
})
export class ChatEmojiService
{
  private readonly emojiData: Observable<ChatEmojiData>;

  constructor(@Inject(APP_BASE_HREF) baseHref: string)
  {
    const normalizedBase = baseHref.endsWith('/') ? baseHref : `${baseHref}/`;
    const defaultUrl = `${normalizedBase}assets/chat/emojis.json`;
    const customUrl = `${normalizedBase}assets/chat/emojis-custom.json`;

    // Local static assets — use fetch so API HTTP interceptors are not applied.
    this.emojiData = from(this.loadEmojiData(defaultUrl, customUrl)).pipe(shareReplay(1));
  }

  public getEmojis(): Observable<ChatEmojiData>
  {
    return this.emojiData;
  }

  private async loadEmojiData(defaultUrl: string, customUrl: string): Promise<ChatEmojiData>
  {
    const baseResponse = await fetch(defaultUrl);
    if (!baseResponse.ok)
    {
      throw new Error(`Failed to load chat emojis (${baseResponse.status})`);
    }

    const base = await baseResponse.json() as ChatEmojiData;
    const custom = await this.loadCustomEmojiData(customUrl);

    return custom ? mergeEmojiData(base, custom) : base;
  }

  private async loadCustomEmojiData(customUrl: string): Promise<ChatEmojiData | null>
  {
    let custom: ChatEmojiData | null = null;
    const customResponse = await fetch(customUrl).catch((): null => null);

    if (customResponse?.ok)
    {
      const data = await customResponse.json().catch((): null => null) as ChatEmojiData | null;
      if (data?.categories?.length)
      {
        custom = data;
      }
    }

    return custom;
  }
}
